import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { signupTracker } from '@mobi/account/Common/Analytics'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import { FeatureIcon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons'
import { CustomerServiceCentre, CustomerServiceTelephone } from '@mobi/component-library/Base/Link'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { spacing } from '@mobi/component-library/Foundations'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { callCenterPhoneNumber } from '@mobi/utils/phone'
import { getSignUpState } from '../../Store'
import { routes } from '../../Utils'
import { FormLayout } from '../Verification/Layout.styled'

export const FailedVerification = ({
  showLogin,
}: {
  showLogin: (options?: { onLoginSuccessRedirectUrl?: string }) => void
}) => {
  const navigate = useNavigate()
  const state = useSelector(getSignUpState)
  const [failType, setFailType] = useState<
    | undefined
    | 'closedAccount'
    | 'duplicateAccount'
    | 'unverified'
    | 'newUserAutoLogInFail'
    | 'pendingReview'
    | 'wageringAccountPending'
  >()
  const [header, setHeader] = useState<string>('')
  const [subHeader, setSubHeader] = useState<React.JSX.Element>()

  const verification = state.signupVerification
  const application = state.signupApplication

  useEffect(() => {
    //closed account
    if (verification.duplicateExists && verification.isDuplicateClosed) {
      signupTracker({
        interaction_type: 'Sign Up Failed',
        signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
        message: 'Closed account',
      })
      setFailType('closedAccount')
      setHeader('We were unable to verify your identity.')
      setSubHeader(
        <>
          Unfortunately we couldn't verify your identity with the details you provided, please
          contact us on <CustomerServiceTelephone /> or use the button below.
        </>
      )
    }
    //duplicate account
    else if (verification.duplicateExists && !verification.isDuplicateClosed) {
      signupTracker({
        interaction_type: 'Sign Up Failed',
        signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
        message: 'Duplicate account',
      })
      setFailType('duplicateAccount')
      setHeader('Looks like you already have an account.')
      setSubHeader(
        <>
          Please login to access your account. If you have any issues accessing your account, please
          contact us on <CustomerServiceTelephone /> or use the button below.
        </>
      )
    }
    //dvs id not verified
    else if (!verification.isVerified) {
      signupTracker({
        interaction_type: 'Sign Up Failed',
        signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
        message: 'Unverified',
        isVerified: verification.isVerified,
        isAccountPending: state.isApplicationPendingReview,
        attemptNumber: state.numberOfAttemptsToVerify.toString(),
      })
      setFailType('unverified')
      setHeader('We were unable to verify your identity.')
      setSubHeader(
        <CustomerServiceCentre prefixText='Unfortunately we couldn’t verify your identity with the details you provided. We are unable to create a TABtouch account for you.' />
      )
    }
    //high risk user
    else if (state.isApplicationPendingReview) {
      signupTracker({
        interaction_type: 'Sign Up Failed',
        signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
        message: 'Pending review',
        isVerified: verification.isVerified,
        isAccountPending: state.isApplicationPendingReview,
        attemptNumber: state.numberOfAttemptsToVerify.toString(),
      })
      setFailType('pendingReview')
      setHeader('We were unable to verify your account.')
      setSubHeader(
        <>Our team will review your application to join TABtouch and will be in touch soon.</>
      )
    }
    //bet account creation pending
    else if (state.isWageringAccountPendingCreation) {
      signupTracker({
        interaction_type: 'Sign Up Failed',
        signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
        message: 'Wagering account pending',
        isVerified: verification.isVerified,
        attemptNumber: state.numberOfAttemptsToVerify.toString(),
      })
      setFailType('wageringAccountPending')
      setHeader('We were unable to create your account.')
      setSubHeader(
        <>
          Unfortunately we couldn't create your account, please contact us on{' '}
          <CustomerServiceTelephone /> or use the button below.
        </>
      )
    }
    //auto login failed
    else if (
      !state.isNewUserAutoLoggedIn &&
      !state.isApplicationPendingReview &&
      verification?.isVerified &&
      !verification?.duplicateExists
    ) {
      setFailType('newUserAutoLogInFail')
      setHeader('We have created your account, but encountered an issue logging you in.')
      setSubHeader(
        <>
          Please try logging in to set your deposit limit and start betting. For ongoing issues
          please contact us on <CustomerServiceTelephone />.
        </>
      )
    }
  }, [state])

  if (verification.isVerified && !verification.duplicateExists && state.isNewUserAutoLoggedIn) {
    return null
  }

  const contactUs = () => {
    signupTracker({
      interaction_type: 'Need Assistance',
      signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
      location: 'Sign Up Failed',
    })
    window.location.href = callCenterPhoneNumber
  }

  return (
    <FormLayout>
      <Flex flexDirection='column' gap={spacing.sm}>
        <FeatureIcon type='warning' size='xl' />
        <AccountHeader
          alignItems='start'
          title={header}
          description={subHeader}
          style={{ marginBottom: '0rem' }}
        />
      </Flex>
      {failType === 'unverified' && (
        <InfoBox color='blue' size='sm'>
          <div>
            Please use reference <b>#{application.applicationReferenceNumber}</b> and have a
            suitable ID at hand for the call.
          </div>
        </InfoBox>
      )}

      <footer style={{ rowGap: spacing.sm }}>
        {(failType === 'closedAccount' || failType === 'wageringAccountPending') && (
          <Button isFullWidth onClick={contactUs}>
            Call now
          </Button>
        )}

        {failType === 'duplicateAccount' && (
          <>
            <Button isFullWidth onClick={() => showLogin({ onLoginSuccessRedirectUrl: '/' })}>
              Login
            </Button>
            <Button color='tertiary_color' isFullWidth onClick={contactUs}>
              Call support team
            </Button>
          </>
        )}

        {failType === 'pendingReview' && (
          <Button isFullWidth onClick={() => navigate('/')}>
            Close
          </Button>
        )}

        {failType === 'unverified' && (
          <>
            <Button
              isFullWidth
              onClick={() => {
                navigate(routes.verificationOptions)
              }}
            >
              Try another ID document
            </Button>
            <Button color='tertiary_color' isFullWidth onClick={contactUs}>
              Call now
            </Button>
          </>
        )}
        {failType === 'newUserAutoLogInFail' && (
          <>
            <Button isFullWidth onClick={() => showLogin({ onLoginSuccessRedirectUrl: '/' })}>
              Login
            </Button>
            <Button color='tertiary_color' isFullWidth onClick={contactUs}>
              Call support team
            </Button>
          </>
        )}
      </footer>
    </FormLayout>
  )
}
